import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'

const COMMIT_DELAY_MS = 300

interface InboxTextFilterInputProps {
  value: string
  onChange: (value: string) => void
  placeholder: string
  inputMode?: 'text' | 'tel'
}

export function InboxTextFilterInput({
  value,
  onChange,
  placeholder,
  inputMode = 'text',
}: InboxTextFilterInputProps) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState(value)
  const onChangeRef = useRef(onChange)
  const valueRef = useRef(value)
  const draftRef = useRef(value)
  const timerRef = useRef<number | null>(null)

  useEffect(() => {
    onChangeRef.current = onChange
    valueRef.current = value
  }, [onChange, value])

  const commit = (next: string) => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
    const trimmed = next.trim()
    draftRef.current = trimmed
    setDraft(trimmed)
    if (trimmed !== valueRef.current.trim()) onChangeRef.current(trimmed)
  }

  // Closing the menu or switching category unmounts the input before the delay runs out
  useEffect(() => {
    return () => {
      if (!timerRef.current) return
      window.clearTimeout(timerRef.current)
      const trimmed = draftRef.current.trim()
      if (trimmed !== valueRef.current.trim()) onChangeRef.current(trimmed)
    }
  }, [])

  const handleDraftChange = (next: string) => {
    draftRef.current = next
    setDraft(next)
    if (timerRef.current) window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => commit(next), COMMIT_DELAY_MS)
  }

  return (
    <>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <Search
          className="size-4 shrink-0 text-gray-700 dark:text-gray-300"
          strokeWidth={2.5}
          aria-hidden="true"
        />
        <input
          type="text"
          inputMode={inputMode}
          value={draft}
          onChange={(event) => handleDraftChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              commit(draft)
            }
          }}
          placeholder={placeholder}
          aria-label={placeholder}
          className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-hidden! placeholder:text-gray-400 dark:text-gray-100"
          autoFocus
        />
      </div>
      {draft.trim() !== '' && (
        <>
          <div className="h-px bg-gray-200 dark:bg-gray-700" />
          <button
            type="button"
            className="w-full cursor-pointer px-3 py-2 text-left text-sm leading-[18px] text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800"
            onClick={() => commit('')}
          >
            {t('resources.generic.clear')}
          </button>
        </>
      )}
    </>
  )
}
